/**
 * /admin/returns — Returns (arc Inc 6). Admin-only.
 *
 * The returns analogue of the Fees View-History, with the SAME structure + options
 * (Pedro 2026-06-26): a Per-Agreement / Per-Month toggle, a period picker, and an
 * accordion of months (per agreement) or agreements (per month) that expands to the
 * per-account returns.
 *
 * Returns blend uploaded historical Return% (< Jan-2026) with computed TWR (>= Jan-2026;
 * opening-anchor + sub-period linking, SAA-proxy / manual-MTD interim rungs — modified
 * Dietz was retired at C-276). Each computed month shows its fidelity rung (Exact / Manual
 * MTD / SAA proxy / Gap; procedure §8). The per-agreement Total shows the PERIOD return:
 * cumulative (geometric link) + annualized (— when the window < 12 months). Money is not
 * shown here — only returns, as ratios rendered as signed percentages.
 */
import { Fragment, useMemo, useState } from 'react';
import {
  ActionIcon, Badge, Button, Container, Group, Loader, Paper, SegmentedControl,
  Stack, Table, Text, Title, Tooltip,
} from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import {
  IconAlertTriangle, IconChevronDown, IconChevronRight, IconFilterOff, IconReportSearch, IconTrendingUp,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';

import { AgreementPicker } from '../components/EntityPicker';
import { BackToCompliance } from '../components/BackToCompliance';
import { RequireAccess } from '../components/RequireAccess';
import { PeriodRangeSelect, resolveWindow } from '../components/periodRange';
import type { DataSpan, PeriodPreset } from '../components/periodRange';
import { returnsApi, type AgreementReturnMonth, type Fidelity, type FirmwideReturnRow } from '../api/returns';
import {
  ColumnMultiSelectFilter, ColumnSelectFilter, ColumnTextFilter, hasActiveFilters, textMatches,
} from '../components/ColumnFilters';
import { SortableTh, nextSort, sortRows, type SortState } from '../components/SortableHeader';
import { getDataSpan } from '../api/reports';
import {
  FIDELITY, FidelityPill, ReturnAuditModal, ReturnBadge, fmtPct, retColor,
} from '../components/ReturnAudit';
import type { AuditTarget } from '../components/ReturnAudit';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import type { UUID } from '../api/types';

// ── helpers ──────────────────────────────────────────────────────────────
// fmtPct / retColor / ReturnBadge / FIDELITY / FidelityPill and the audit modal live in
// components/ReturnAudit.tsx (shared with the Agreement → AuM & Return tab, P8).
function fmtMonth(periodMonth: string): string {
  const [y, m] = periodMonth.slice(0, 7).split('-').map(Number);
  if (!y || !m) return periodMonth.slice(0, 7);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

/** One-line legend of the four fidelity rungs (shown with the data it annotates). */
function FidelityLegend() {
  return (
    <Group gap="xs" wrap="wrap">
      <Text size="xs" c="dimmed">Basis:</Text>
      {(['exact', 'manual', 'saa', 'gap'] as Fidelity[]).map((k) => (
        <Tooltip key={k} label={FIDELITY[k].hint} withArrow multiline w={280}>
          <Badge size="xs" variant="light" color={FIDELITY[k].color} style={{ cursor: 'help' }}>
            {FIDELITY[k].label}
          </Badge>
        </Tooltip>
      ))}
    </Group>
  );
}

function FlagsNote({ flags }: { flags: string[] }) {
  if (!flags.length) return null;
  return (
    <Tooltip label={flags.join(' · ')} withArrow>
      <Badge color="bordeaux" variant="filled" leftSection={<IconAlertTriangle size={12} />} style={{ cursor: 'help' }}>
        {flags.length} flag{flags.length === 1 ? '' : 's'}
      </Badge>
    </Tooltip>
  );
}

/** Per-account return rows for a computed month (historical months carry none). */
function AccountReturnTable({ m }: { m: AgreementReturnMonth }) {
  if (m.source === 'historical') {
    return <Text size="sm" c="dimmed">Uploaded historical agreement return — no per-account detail.</Text>;
  }
  if (!m.accounts.length) {
    return <Text size="sm" c="dimmed">No contributing accounts this month.</Text>;
  }
  return (
    <Table withTableBorder striped verticalSpacing="xs">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Account</Table.Th>
          <Table.Th>Basis</Table.Th>
          <Table.Th ta="right">Return</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {m.accounts.map((a) => (
          <Table.Tr key={a.account_id}>
            <Table.Td><Text size="sm">{a.label}</Text></Table.Td>
            <Table.Td><FidelityPill fidelity={a.fidelity} reason={a.fidelity_reason} /></Table.Td>
            <Table.Td ta="right"><ReturnBadge r={a.return_ratio} /></Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

/** An accordion panel with a "Show working" affordance (computed months only) above the
 * per-account table. Shared by the per-agreement and per-month views. */
function AuditablePanel({ agreementId, m, onAudit }:
  { agreementId: UUID; m: AgreementReturnMonth; onAudit: (t: AuditTarget) => void }) {
  return (
    <>
      {m.source === 'computed' && (
        <Group justify="flex-end" mb="xs">
          <Button
            size="xs" variant="light" leftSection={<IconReportSearch size={14} />}
            onClick={() => onAudit({ agreementId, period: m.period_month.slice(0, 7), label: fmtMonth(m.period_month) })}
          >
            Show working
          </Button>
        </Group>
      )}
      <AccountReturnTable m={m} />
    </>
  );
}

// ── P44 (Pedro 2026-10-15): the two views as tables with sortable + filterable headers ──
const RETURN_FILTER_OPTS = [
  { value: 'pos', label: 'Positive' }, { value: 'neg', label: 'Negative' }, { value: 'none', label: 'No return' },
];
const FLAG_FILTER_OPTS = [{ value: 'with', label: 'With flags' }, { value: 'without', label: 'No flags' }];
const BASIS_OPTS = (['exact', 'manual', 'saa', 'gap'] as Fidelity[]).map((k) => ({ value: k, label: FIDELITY[k].label }));

function matchesReturn(r: number | null, f: string | null): boolean {
  if (!f) return true;
  if (f === 'none') return r == null;
  if (r == null) return false;
  return f === 'pos' ? r >= 0 : r < 0;
}
function matchesFlags(n: number, f: string | null): boolean {
  return !f || (f === 'with' ? n > 0 : n === 0);
}

/** Expandable table rows: click a row to show its per-account detail underneath. */
function useOpenRows() {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (id: string) => setOpen((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  return { open, toggle };
}

type MonthKey = 'code' | 'mnemonic' | 'saa' | 'rm' | 'basis' | 'flags' | 'ret';

function MonthReturnsTable({ rows, onAudit }: { rows: FirmwideReturnRow[]; onAudit: (t: AuditTarget) => void }) {
  const [sort, setSort] = useState<SortState<MonthKey> | null>({ key: 'code', dir: 'asc' });
  const [codeQ, setCodeQ] = useState('');
  const [mnemQ, setMnemQ] = useState('');
  const [saaF, setSaaF] = useState<string[]>([]);
  const [rmF, setRmF] = useState<string[]>([]);
  const [basisF, setBasisF] = useState<string[]>([]);
  const [flagsF, setFlagsF] = useState<string | null>(null);
  const [retF, setRetF] = useState<string | null>(null);
  const { open, toggle } = useOpenRows();
  const filtersActive = hasActiveFilters({ codeQ, mnemQ, saaF, rmF, basisF, flagsF, retF });
  const clear = () => { setCodeQ(''); setMnemQ(''); setSaaF([]); setRmF([]); setBasisF([]); setFlagsF(null); setRetF(null); };

  const saaOpts = [...new Set(rows.map((r) => r.saa).filter(Boolean) as string[])].sort()
    .map((v) => ({ value: v, label: v }));
  const rmOpts = [...new Set(rows.map((r) => r.rm_label ?? '—'))].sort().map((v) => ({ value: v, label: v }));

  const shown = sortRows(
    rows.filter((r) =>
      textMatches(r.agreement_code, codeQ)              // P41: "-" = blank
      && textMatches(r.agreement_mnemonic, mnemQ)
      && (saaF.length === 0 || saaF.includes(r.saa ?? ''))
      && (rmF.length === 0 || rmF.includes(r.rm_label ?? '—'))
      && (basisF.length === 0 || basisF.includes(r.month.fidelity ?? ''))
      && matchesFlags(r.month.flags.length, flagsF)
      && matchesReturn(r.month.return_ratio, retF)),
    sort,
    (r, k) => ({
      code: r.agreement_code, mnemonic: r.agreement_mnemonic, saa: r.saa, rm: r.rm_label,
      basis: r.month.fidelity ? FIDELITY[r.month.fidelity]?.label : null,
      flags: r.month.flags.length, ret: r.month.return_ratio,
    })[k],
  );
  const onSort = (k: MonthKey) => setSort((s) => nextSort(s, k));

  return (
    <Table.ScrollContainer minWidth={900}>
      <Table withTableBorder highlightOnHover verticalSpacing="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={32} />
            <SortableTh label="Code" col="code" sort={sort} onSort={onSort} filtered={!!codeQ} />
            <SortableTh label="Mnemonic" col="mnemonic" sort={sort} onSort={onSort} filtered={!!mnemQ} />
            <SortableTh label="SAA" col="saa" sort={sort} onSort={onSort} filtered={saaF.length > 0} />
            <SortableTh label="RM" col="rm" sort={sort} onSort={onSort} filtered={rmF.length > 0} />
            <SortableTh label="Basis" col="basis" sort={sort} onSort={onSort} filtered={basisF.length > 0} />
            <SortableTh label="Flags" col="flags" sort={sort} onSort={onSort} filtered={!!flagsF} />
            <SortableTh label="Return" col="ret" sort={sort} onSort={onSort} filtered={!!retF} ta="right" />
          </Table.Tr>
          <Table.Tr>
            <Table.Th>
              {filtersActive && (
                <Tooltip label="Clear all column filters" withArrow>
                  <ActionIcon size="sm" variant="subtle" color="gray" onClick={clear} aria-label="Clear filters">
                    <IconFilterOff size={14} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Table.Th>
            <Table.Th><ColumnTextFilter value={codeQ} onChange={setCodeQ} placeholder="Code…" ariaLabel="Filter by code" /></Table.Th>
            <Table.Th><ColumnTextFilter value={mnemQ} onChange={setMnemQ} placeholder="Mnemonic…" ariaLabel="Filter by mnemonic" /></Table.Th>
            <Table.Th><ColumnMultiSelectFilter values={saaF} onChange={setSaaF} options={saaOpts} allLabel="Any" ariaLabel="Filter by SAA" /></Table.Th>
            <Table.Th><ColumnMultiSelectFilter values={rmF} onChange={setRmF} options={rmOpts} allLabel="Any" ariaLabel="Filter by RM" /></Table.Th>
            <Table.Th><ColumnMultiSelectFilter values={basisF} onChange={setBasisF} options={BASIS_OPTS} allLabel="Any" ariaLabel="Filter by basis" /></Table.Th>
            <Table.Th><ColumnSelectFilter value={flagsF} onChange={setFlagsF} options={FLAG_FILTER_OPTS} allLabel="Any" ariaLabel="Filter by flags" /></Table.Th>
            <Table.Th><ColumnSelectFilter value={retF} onChange={setRetF} options={RETURN_FILTER_OPTS} allLabel="Any" ariaLabel="Filter by return" /></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {shown.length === 0 && (
            <Table.Tr><Table.Td colSpan={8}><Text size="sm" c="dimmed" py="xs">No agreements match the current filters.</Text></Table.Td></Table.Tr>
          )}
          {shown.map((row) => {
            const isOpen = open.has(row.agreement_id);
            return (
              <Fragment key={row.agreement_id}>
                <Table.Tr style={{ cursor: 'pointer' }} onClick={() => toggle(row.agreement_id)}>
                  <Table.Td>{isOpen ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}</Table.Td>
                  <Table.Td><Text size="sm" fw={600}>{row.agreement_code ?? '—'}</Text></Table.Td>
                  <Table.Td><Text size="sm">{row.agreement_mnemonic ?? '—'}</Text></Table.Td>
                  <Table.Td><Text size="sm">{row.saa ?? '—'}</Text></Table.Td>
                  <Table.Td><Text size="sm">{row.rm_label ?? '—'}</Text></Table.Td>
                  <Table.Td><FidelityPill fidelity={row.month.fidelity} reason={row.month.fidelity_reason} /></Table.Td>
                  <Table.Td><FlagsNote flags={row.month.flags} /></Table.Td>
                  <Table.Td ta="right"><ReturnBadge r={row.month.return_ratio} /></Table.Td>
                </Table.Tr>
                {isOpen && (
                  <Table.Tr>
                    <Table.Td />
                    <Table.Td colSpan={7}><AuditablePanel agreementId={row.agreement_id} m={row.month} onAudit={onAudit} /></Table.Td>
                  </Table.Tr>
                )}
              </Fragment>
            );
          })}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

type AgrKey = 'month' | 'source' | 'basis' | 'flags' | 'ret';

function AgreementMonthsTable({ agreementId, months, onAudit }:
  { agreementId: UUID; months: AgreementReturnMonth[]; onAudit: (t: AuditTarget) => void }) {
  const [sort, setSort] = useState<SortState<AgrKey> | null>(null);   // null = the feed's own order
  const [sourceF, setSourceF] = useState<string | null>(null);
  const [basisF, setBasisF] = useState<string[]>([]);
  const [flagsF, setFlagsF] = useState<string | null>(null);
  const [retF, setRetF] = useState<string | null>(null);
  const { open, toggle } = useOpenRows();
  const filtersActive = hasActiveFilters({ sourceF, basisF, flagsF, retF });
  const clear = () => { setSourceF(null); setBasisF([]); setFlagsF(null); setRetF(null); };

  const shown = sortRows(
    months.filter((m) =>
      (!sourceF || m.source === sourceF)
      && (basisF.length === 0 || basisF.includes(m.fidelity ?? ''))
      && matchesFlags(m.flags.length, flagsF)
      && matchesReturn(m.return_ratio, retF)),
    sort,
    (m, k) => ({
      month: m.period_month, source: m.source, basis: m.fidelity ? FIDELITY[m.fidelity]?.label : null,
      flags: m.flags.length, ret: m.return_ratio,
    })[k],
  );
  const onSort = (k: AgrKey) => setSort((s) => nextSort(s, k));

  return (
    <Table withTableBorder highlightOnHover verticalSpacing="xs">
      <Table.Thead>
        <Table.Tr>
          <Table.Th w={32} />
          <SortableTh label="Month" col="month" sort={sort} onSort={onSort} />
          <SortableTh label="Source" col="source" sort={sort} onSort={onSort} filtered={!!sourceF} />
          <SortableTh label="Basis" col="basis" sort={sort} onSort={onSort} filtered={basisF.length > 0} />
          <SortableTh label="Flags" col="flags" sort={sort} onSort={onSort} filtered={!!flagsF} />
          <SortableTh label="Return" col="ret" sort={sort} onSort={onSort} filtered={!!retF} ta="right" />
        </Table.Tr>
        <Table.Tr>
          <Table.Th>
            {filtersActive && (
              <Tooltip label="Clear all column filters" withArrow>
                <ActionIcon size="sm" variant="subtle" color="gray" onClick={clear} aria-label="Clear filters">
                  <IconFilterOff size={14} />
                </ActionIcon>
              </Tooltip>
            )}
          </Table.Th>
          <Table.Th />
          <Table.Th><ColumnSelectFilter value={sourceF} onChange={setSourceF}
            options={[{ value: 'computed', label: 'Computed' }, { value: 'historical', label: 'Historical' }]}
            allLabel="Any" ariaLabel="Filter by source" /></Table.Th>
          <Table.Th><ColumnMultiSelectFilter values={basisF} onChange={setBasisF} options={BASIS_OPTS} allLabel="Any" ariaLabel="Filter by basis" /></Table.Th>
          <Table.Th><ColumnSelectFilter value={flagsF} onChange={setFlagsF} options={FLAG_FILTER_OPTS} allLabel="Any" ariaLabel="Filter by flags" /></Table.Th>
          <Table.Th><ColumnSelectFilter value={retF} onChange={setRetF} options={RETURN_FILTER_OPTS} allLabel="Any" ariaLabel="Filter by return" /></Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {shown.length === 0 && (
          <Table.Tr><Table.Td colSpan={6}><Text size="sm" c="dimmed" py="xs">No months match the current filters.</Text></Table.Td></Table.Tr>
        )}
        {shown.map((m) => {
          const isOpen = open.has(m.period_month);
          return (
            <Fragment key={m.period_month}>
              <Table.Tr style={{ cursor: 'pointer' }} onClick={() => toggle(m.period_month)}>
                <Table.Td>{isOpen ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}</Table.Td>
                <Table.Td><Text size="sm" fw={600}>{fmtMonth(m.period_month)}</Text></Table.Td>
                <Table.Td>
                  <Badge size="xs" variant="light" color={m.source === 'computed' ? 'blue' : 'gray'}>
                    {m.source === 'computed' ? 'Computed' : 'Historical'}
                  </Badge>
                </Table.Td>
                <Table.Td><FidelityPill fidelity={m.fidelity} reason={m.fidelity_reason} /></Table.Td>
                <Table.Td><FlagsNote flags={m.flags} /></Table.Td>
                <Table.Td ta="right"><ReturnBadge r={m.return_ratio} /></Table.Td>
              </Table.Tr>
              {isOpen && (
                <Table.Tr>
                  <Table.Td />
                  <Table.Td colSpan={5}><AuditablePanel agreementId={agreementId} m={m} onAudit={onAudit} /></Table.Td>
                </Table.Tr>
              )}
            </Fragment>
          );
        })}
      </Table.Tbody>
    </Table>
  );
}

// ── component ──────────────────────────────────────────────────────────────
// Exported so the Portfolio Data page (nav reorg 2026-07-04, Inc 4) can render it
// as its "Returns" tab; ReturnsPage (below) is kept for the redirected /admin/returns.
export function ReturnsSection() {
  const [view, setView] = useState<'agreement' | 'month'>('agreement');
  const [agreementId, setAgreementId] = useState<UUID | null>(null);
  const [preset, setPreset] = useState<PeriodPreset>('12m');
  const [since, setSince] = useState<Date | null>(null);
  const [month, setMonth] = useState('');                  // 'YYYY-MM-01' (Per-Month view)
  const [audit, setAudit] = useState<AuditTarget | null>(null);   // §10 "show working" modal target

  // Per-agreement data span — windows anchor on the PICKED agreement's own history
  // (Last Nm = N months with data, Max = its inception -> latest), not the calendar.
  const spanQ = useQuery({
    queryKey: ['data-span', agreementId ?? ''] as const,
    queryFn: () => getDataSpan(agreementId as string),
    enabled: !!agreementId,
  });
  const span: DataSpan = {
    first: spanQ.data?.first_month ? spanQ.data.first_month.slice(0, 7) : null,
    last: spanQ.data?.last_month ? spanQ.data.last_month.slice(0, 7) : null,
  };
  const win = resolveWindow(preset, since, span);

  const agrQ = useQuery({
    queryKey: ['returns', 'agreement', agreementId ?? '', win?.from ?? '', win?.to ?? ''] as const,
    queryFn: () => returnsApi.agreement(agreementId as UUID, win!.from, win!.to),
    enabled: view === 'agreement' && !!agreementId && !!win,
  });
  const monthQ = useQuery({
    queryKey: ['returns', 'month', month] as const,
    queryFn: () => returnsApi.month(month.slice(0, 7)),
    enabled: view === 'month' && !!month,
  });
  const monthRows = useMemo(() => (Array.isArray(monthQ.data) ? monthQ.data : []), [monthQ.data]);

  return (
    <Stack>
      <ReturnAuditModal target={audit} onClose={() => setAudit(null)} fetchAudit={returnsApi.audit} />
      <SegmentedControl
        value={view}
        onChange={(v) => setView(v as 'agreement' | 'month')}
        data={[{ label: 'Per agreement', value: 'agreement' }, { label: 'Per month', value: 'month' }]}
        maw={320}
      />

      {view === 'agreement' && (
      <>
      <Group align="flex-end" wrap="wrap" gap="md">
        <AgreementPicker
          label="Agreement"
          placeholder="Pick an agreement to see its returns"
          value={agreementId}
          onChange={setAgreementId}
        />
        <PeriodRangeSelect preset={preset} onPreset={setPreset} since={since} onSince={setSince} />
      </Group>

      {!agreementId && <Text size="sm" c="dimmed">Pick an agreement above to view its returns.</Text>}
      {agreementId && preset === 'since' && !since && (
        <Text size="sm" c="dimmed">Pick a start month to total from.</Text>
      )}
      {agrQ.isLoading && <Group gap="xs"><Loader size="sm" /><Text size="sm" c="dimmed">Loading…</Text></Group>}

      {agrQ.data && (
        <Paper withBorder p="md" radius="md">
          <Group justify="space-between" wrap="wrap">
            <Text fw={600}>Period return</Text>
            <Group gap="xl" wrap="nowrap">
              <Stack gap={0} align="flex-end">
                <Text size="xs" c="dimmed">Effective (cumulative)</Text>
                <Text size="md" fw={700} c={retColor(agrQ.data.summary.cumulative)}>
                  {fmtPct(agrQ.data.summary.cumulative)}
                </Text>
              </Stack>
              <Stack gap={0} align="flex-end">
                <Text size="xs" c="dimmed">Annualized</Text>
                <Text size="md" fw={700} c={retColor(agrQ.data.summary.annualized)}>
                  {fmtPct(agrQ.data.summary.annualized)}
                </Text>
              </Stack>
            </Group>
          </Group>
          <Text size="xs" c="dimmed" mt={6}>
            {win ? `${fmtMonth(win.from)} – ${fmtMonth(win.to)}` : '—'} · {agrQ.data.summary.n_months} month
            {agrQ.data.summary.n_months === 1 ? '' : 's'} · {agrQ.data.summary.n_with_return} with a return
            {agrQ.data.summary.annualized == null && agrQ.data.summary.cumulative != null
              ? ' · annualized shown only for windows ≥ 12 months' : ''}
          </Text>
        </Paper>
      )}

      {agrQ.data && agrQ.data.months.length > 0 && (
        <>
        <FidelityLegend />
        <AgreementMonthsTable agreementId={agreementId as UUID} months={agrQ.data.months} onAudit={setAudit} />
        </>
      )}
      {agrQ.data && agrQ.data.months.length === 0 && (
        <Text size="sm" c="dimmed">No months in the selected period.</Text>
      )}
      </>
      )}

      {view === 'month' && (
      <>
      <MonthPickerInput
        label="Month"
        placeholder="Pick a month to see every agreement's return"
        value={month ? new Date(`${month}T00:00:00`) : null}
        onChange={(d) => setMonth(d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01` : '')}
        maw={240}
      />
      {!month && <Text size="sm" c="dimmed">Pick a month above to view every agreement's return.</Text>}
      {monthQ.isLoading && <Group gap="xs"><Loader size="sm" /><Text size="sm" c="dimmed">Loading…</Text></Group>}
      {month && !monthQ.isLoading && monthRows.length === 0 && (
        <Text size="sm" c="dimmed">No agreement returns for this month.</Text>
      )}
      {monthRows.length > 0 && (
        <>
        <FidelityLegend />
        <MonthReturnsTable rows={monthRows} onAudit={setAudit} />
        </>
      )}
      </>
      )}
    </Stack>
  );
}

export function ReturnsPage() {
  useDocumentTitle('Returns');
  return (
    <RequireAccess roles={['admin']}>
      <Container size="xl">
        <Stack gap="lg">
          <BackToCompliance to="/admin" label="Back to Admin" />
          <Group gap="xs"><IconTrendingUp size={22} /><Title order={2}>Returns</Title></Group>
          <Text c="dimmed">
            Per-account and per-agreement Time-Weighted returns (Jan-2026 onward),
            blended with uploaded historical agreement returns. Each computed month
            shows the basis used to value it (Exact / Manual MTD / SAA proxy / Gap).
            Period figures are compounded and annualized.
          </Text>
          <ReturnsSection />
        </Stack>
      </Container>
    </RequireAccess>
  );
}
